import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  type Locale,
} from '@cibli/shared';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';

import { LangBridge } from '@/bridge/lang.bridge';
import { useLanguageContext } from '@/features/common/contexts/language.context';
import { useUserContext } from '@/features/common/contexts/user.context';
import { getLocales } from '@/services/locale';
import { updateUserLocale } from '@/services/user';

interface LanguageSelectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function LanguageSelectModal({ open, onOpenChange }: LanguageSelectModalProps) {
  const { t, i18n } = useTranslation();
  const { setLanguage, writeFastpassLanguage } = useLanguageContext();
  const { user } = useUserContext();
  const [locales, setLocales] = useState<Locale[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let isMounted = true;
    setLoading(true);
    getLocales()
      .then(data => {
        if (isMounted) setLocales(data);
      })
      .catch(err => {
        console.error('Failed to load locales in modal:', err);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [open]);

  // Current active language (match on codeLong or base code)
  const currentLanguage = i18n.language;

  const sortedLocales = useMemo(() => {
    return [...locales].sort((a, b) => {
      const aIsActive =
        a.codeLong === currentLanguage ||
        a.codeShort === currentLanguage ||
        a.codeI18n === currentLanguage;
      const bIsActive =
        b.codeLong === currentLanguage ||
        b.codeShort === currentLanguage ||
        b.codeI18n === currentLanguage;
      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [locales, currentLanguage]);

  const handleSelect = async (codeLong: string) => {
    try {
      await setLanguage(codeLong);
      writeFastpassLanguage(codeLong);

      // Persist to account when user is logged in
      if (user?.id) {
        try {
          await updateUserLocale(codeLong, { showErrorToast: false });
        } catch {
          // non-blocking
        }
      }

      await LangBridge.setLanguage(codeLong);
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to set language:', err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            {t('LanguageSelector.title', 'Choisissez votre langue')}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {t(
              'LanguageSelector.subtitle',
              'Sélectionnez la langue utilisée pour votre parcours et l’interview.',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          {loading && locales.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {t('LanguageSelector.loading', 'Chargement des langues...')}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 max-h-[60vh] overflow-y-auto pr-1">
              {sortedLocales.map((locale, idx) => {
                const countryCode = locale.codeLong.split('-')[1] || locale.codeShort.toUpperCase();
                const isSelected =
                  currentLanguage === locale.codeLong ||
                  currentLanguage === locale.codeShort ||
                  currentLanguage === locale.codeI18n;

                return (
                  <motion.div
                    key={locale.codeLong}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.18, delay: Math.min(idx * 0.03, 0.25) }}
                  >
                    <Button
                      type="button"
                      variant={isSelected ? 'default' : 'outline'}
                      className={`w-full justify-between h-14 px-4 rounded-xl text-left font-medium transition-all ${
                        isSelected
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'hover:bg-accent hover:text-accent-foreground'
                      }`}
                      onClick={() => handleSelect(locale.codeLong)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="size-8 rounded-full overflow-hidden flex items-center justify-center shrink-0 border border-black/10 bg-background/50">
                          <ReactCountryFlag
                            countryCode={countryCode}
                            svg
                            style={{
                              width: 22,
                              height: 22,
                              borderRadius: '50%',
                              objectFit: 'cover',
                            }}
                          />
                        </div>
                        <div className="flex flex-col truncate">
                          <span className="text-base font-semibold leading-tight truncate">
                            {locale.name}
                          </span>
                          <span className="text-xs opacity-75 font-mono">{locale.codeLong}</span>
                        </div>
                      </div>

                      {isSelected && (
                        <div className="size-6 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                          <Check className="size-4" strokeWidth={3} />
                        </div>
                      )}
                    </Button>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
